import { useState, useEffect } from "react";
import { DateTime } from "luxon";
import { subtractCoinsForCurrentUser, getUserProfile } from "../../utilities/axios.utilities";
import Header from "../../components/Header";
import ModalConfirmLogout from "../../components/ModalConfirmLogout";
import styles from './style.module.css';
import "./lobbyRoomHistory.css";
import { getDataLocal, getResultCaculator, addDataToLocal, initDataLocal } from "../../utilities/caculator.utilities";
import Swal from "sweetalert2";

interface Profile {
  userName: string;
  name: string;
  phone: string;
  coins: number;
}

export default function MainGameBacarat() {
  const [isShowLogout, setIsShowLogout] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isCalculating, setIsCalculating] = useState(false);
  const [predictionWinPct, setPredictionWinPct] = useState<number>(88);
  const [progress, setProgress] = useState(0.0000);
  const [tableNumber, setTableNumber] = useState('');
  const [displayTableNumber, setDisplayTableNumber] = useState('');
  const [isTableNumberLocked, setIsTableNumberLocked] = useState(false);
  const [playerValue, setPlayerValue] = useState<number | ''>('');
  const [bankerValue, setBankerValue] = useState<number | ''>('');
  const [dataLocal, setDataLocal] = useState(getDataLocal());
  const [resultTotal, setResultTotal] = useState('_');
  const [resultDetail, setResultDetail] = useState('_');
  const [profile, setProfile] = useState<Profile | null>(null);
  const [nowTick, setNowTick] = useState(() => Date.now());
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const userProfile = await getUserProfile();
        console.log(userProfile);
        setProfile({
          userName: userProfile.username,
          name: userProfile.username,
          phone: userProfile.phone,
          coins: userProfile.coins
        });
      } catch (error) {
        console.error('Error fetching profile:', error);
      }
    };

    fetchProfile();
  }, []);

  useEffect(() => {
    const t = window.setInterval(() => setNowTick(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, []);

  function formatLogTime(stampTime?: number): string {
    if (!stampTime) return "--:--:--";
    try {
      return DateTime.fromMillis(stampTime).setZone("Asia/Ho_Chi_Minh").toFormat("HH:mm:ss");
    } catch {
      return "--:--:--";
    }
  }

  function resultDetailToSide(detail?: string): "PLAYER" | "BANKER" | "HÒA" {
    if (!detail) return "HÒA";
    if (detail === "Con") return "PLAYER";
    if (detail === "Cái" || detail === "Cai") return "BANKER";
    return "HÒA";
  }

  function pillClassForSide(label: string): string {
    if (label === "PLAYER") return "room-pill--player";
    if (label === "BANKER") return "room-pill--banker";
    if (label === "HÒA") return "room-pill--hoa-kq";
    return "room-pill--empty";
  }

  function pillClassForDanhGia(l: "THẮNG" | "THUA" | "HÒA" | "—"): string {
    if (l === "—") return "room-pill--dg-none";
    if (l === "HÒA") return "room-pill--dg-hoa";
    if (l === "THẮNG") return "room-pill--dg-win";
    return "room-pill--dg-lose";
  }

  function evaluateDanhGia(
    duDoan: string,
    ketQua: "PLAYER" | "BANKER" | "HÒA"
  ): "THẮNG" | "THUA" | "HÒA" | "—" {
    if (ketQua === "HÒA") return "HÒA";
    if (duDoan === "—") return "—";
    return duDoan === ketQua ? "THẮNG" : "THUA";
  }

  function getSessionDisplayId(item: any, index: number): string {
    const stamp = item?.stampTime;
    const sid = item?.sessionId ?? item?.sessionID;
    if (sid != null && String(sid).length > 0) return `#${sid}`;
    if (stamp != null) return `#${String(stamp)}`;
    if (item?.id != null) return `#${String(item.id)}`;
    return `#${index}`;
  }

  // Load prediction (resultNext) if Admin already set it.
  // This prevents the mobile prediction box showing "..." before user clicks calculate.
  useEffect(() => {
    try {
      const storedPrediction = localStorage.getItem("resultNext");
      if (!storedPrediction) return;
      const data = JSON.parse(storedPrediction) as { result?: number; resultDetail?: string };
      if (data?.resultDetail) {
        const raw = String(data.resultDetail).trim();
        // Normalize to match the render checks (exact "Con" / "Cái")
        const normalized =
          raw === "Con" ||
          raw.toLowerCase().includes("con")
            ? "Con"
            : raw === "Cái" || raw === "Cai" || raw.toLowerCase().includes("cai") || raw.toLowerCase().includes("cái")
              ? "Cái"
              : raw;
        setResultDetail(normalized);
      }
      if (typeof data?.result === "number") {
        setResultTotal(data.result.toString());
      }
    } catch (e) {
      // Ignore malformed localStorage
    }
  }, []);

  const handleUpdateTable = () => {
    if (!tableNumber) return;
    setIsLoading(true);
    setProgress(0.0000);

    const totalSteps = 200;
    const increment = 100 / totalSteps;
    const intervalDuration = 5000 / totalSteps;

    const interval = setInterval(() => {
      setProgress((prev) => {
        const newValue = Number((prev + increment).toFixed(4));

        if (newValue >= 100.0000) {
          clearInterval(interval);
          setIsLoading(false);
          setIsTableNumberLocked(true);
          setDisplayTableNumber(tableNumber);
          return 100.0000;
        }
        return newValue;
      });
    }, intervalDuration);
  };

  const handleCalculate = async () => {
    if (playerValue === '' || bankerValue === '') return;
    // Lưu “dự đoán” đang hiển thị trước khi calculate, để lịch sử có thể so sánh THẮNG/THUA.
    const duDoanDetailForStore =
      resultDetail === "Con" || resultDetail === "Cái" ? resultDetail : undefined;
    try {
      const resultUser = await subtractCoinsForCurrentUser(10);
      setProfile({
        userName: resultUser.username,
        name: resultUser.username,
        phone: resultUser.phone,
        coins: resultUser.coins
      });

      setIsCalculating(true);
      await new Promise(resolve => setTimeout(resolve, 2000));

      const result = getResultCaculator(Number(playerValue), Number(bankerValue));
      addDataToLocal({
        ...result,
        stampTime: Date.now(),
        duDoanDetail: duDoanDetailForStore,
      });
      setDataLocal(getDataLocal());

      setResultTotal(result.result.toString());
      setResultDetail(result.resultDetail);

      setPlayerValue('');
      setBankerValue('');
      setIsCalculating(false);
    } catch (error: any) {
      if (error.response?.status === 400) {
        Swal.fire({
          icon: "error",
          title: "Lỗi",
          text: "Số xu hiện tại không đủ. ",
          timerProgressBar: true,
          customClass: {
            popup: "custom-swal",
            title: "custom-title",
            icon: "custom-icon",
          },
        });
      }
      setIsCalculating(false);
    }
  };

  const handleReset = () => {
    setPlayerValue('');
    setBankerValue('');
    setResultTotal('_');
    setResultDetail('_');
    setTableNumber('');
    setDisplayTableNumber('');
    setIsTableNumberLocked(false);
    initDataLocal();
    setDataLocal([]);
  }

  useEffect(() => {
    const isCon = resultDetail === "Con";
    const isCai = resultDetail === "Cái";
    if (!isCalculating && (isCon || isCai)) {
      const randomPct = Math.floor(85 + Math.random() * 11); // 85..95
      setPredictionWinPct(randomPct);
    }
  }, [isCalculating, resultDetail]);

  const liveClock = DateTime.fromMillis(nowTick).setZone("Asia/Ho_Chi_Minh").toFormat("HH:mm:ss");

  const historyItems = (() => {
    const raw = Array.isArray(dataLocal) ? dataLocal : [];
    const sorted = [...raw].sort((a: any, b: any) => {
      const ta = a?.stampTime ?? a?.id ?? 0;
      const tb = b?.stampTime ?? b?.id ?? 0;
      return ta - tb;
    });
    const last = sorted.slice(-24).reverse();
    return last.map((item: any, index: number) => {
      const ketQuaSide = resultDetailToSide(item?.resultDetail);

      // Du doan: ưu tiên duDoanDetail (nếu đã lưu), nếu không thì dùng resultDetail.
      const duDoanSideRaw = resultDetailToSide(item?.duDoanDetail ?? item?.resultDetail);
      const duDoan = duDoanSideRaw === "HÒA" ? "—" : duDoanSideRaw;

      const danhGia = evaluateDanhGia(duDoan, ketQuaSide);

      return {
        key: `${getSessionDisplayId(item, index)}-${index}`,
        sessionId: getSessionDisplayId(item, index),
        duDoan,
        ketQua: ketQuaSide,
        danhGia,
        stampTime: item?.stampTime as number | undefined,
      };
    });
  })();

  return (
    <div className="min-h-screen flex flex-col">
      <Header setIsShowLogout={() => setIsShowLogout(true)} />
      {isLoading && (
        <div className={`${styles.loadingModal} fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50`}>
          <div className="bg-white p-8 rounded-lg text-center">
            <h2 className="text-2xl font-bold mb-4">Đang tải dữ liệu trò chơi</h2>
            <p className="text-4xl font-bold text-red-600">{progress.toFixed(0)}%</p>
          </div>
        </div>
      )}
      <div className={`${styles.casinoPageBg} lobby-bg flex-1`}>
        <video
          className={styles.casinoPageBgVideo}
          autoPlay
          loop
          muted
          playsInline
          aria-hidden
        >
          <source src="/assets/bg-mb.mp4" type="video/mp4" />
        </video>
        <video
          className={styles.casinoPageBgVideoPc}
          autoPlay
          loop
          muted
          playsInline
          aria-hidden
        >
          <source src="/assets/bg-pc.mp4" type="video/mp4" />
        </video>
        <div className="relative z-10 flex flex-1 flex-col w-full min-h-0">
          <div className="flex flex-wrap my-7 shrink-0">
            <div className="w-1/2 md:w-2/3"></div>
            <div className="w-1/2 md:w-1/3 flex justify-center items-center">
              <a href="/" className="text-white no-underline">
                <div className="text-white px-4 py-2 rounded-lg shadow box-goto-lobby w-[10rem]">
                  Quay lại
                </div>
              </a>
            </div>
          </div>
          <div className={`container mx-auto py-10 px-4 max-w-screen-xl flex-1 ${styles.bacaratContainer}`}>
          <div className={styles.bacaratTable}>
            <div className="flex justify-between">
              <div className={`w-1/3 pr-2`}>
                <div className={styles.bacaratInfo}>
                  <div><span style={{ color: '#00EEFF', fontWeight: 'bold', fontSize: '23px'}}>THÔNG TIN TÀI KHOẢN</span></div>
                  <div>
                    <p>Tên tài khoản: <span className={styles.bacaratInfoValue}>{profile?.userName || 'Username'}</span></p>
                    <p>Họ và Tên: <span className={styles.bacaratInfoValue}>{profile?.userName || 'Username'}</span></p>
                    <p>Số điện thoại: <span className={styles.bacaratInfoValue}>{profile?.phone || 'Phone'}</span></p>
                    <p>
                      <span className={styles.bacaratCodeRow}>
                        <span>CODE:</span>
                        <span className={styles.bacaratCodeValue}>{profile?.coins || '0'}</span>
                      </span>
                    </p>
                  </div>
                  <button style={{ background: 'linear-gradient(to right, #FF0000, #FF8800)', borderRadius: '10px', color: 'white', fontWeight: 'bold', fontSize: '16px' }}>TẶNG CODE</button>
                </div>
              </div>
              <div className="w-2/3 pl-2">
                <div className={`${styles.bacaratCaculator}`}>
                  <div><span style={{ color: '#00EEFF', fontWeight: 'bold', fontSize: '18px' }}>Công thức Bacarat - {displayTableNumber || '?'}</span></div>
                  {/* Desktop layout (giữ nguyên) */}
                  <div className="hidden md:block">
                    <div className={`${styles.bacaratCaculatorControl} flex justify-between items-center`}>
                      <div className="flex items-center gap-2 w-full">
                        <div className="flex items-center gap-2">
                          <p>Số bàn : </p>
                          <input
                            type="text"
                            value={tableNumber}
                            onChange={(e) => setTableNumber(e.target.value)}
                          />
                        </div>

                        <div>
                          <span style={{ fontWeight: 'bold' }}>Sảnh : </span>
                          <select className={`${styles.bacaratCaculatorControlSelect}`}>
                            <option value="1">SEXY</option>
                            <option value="2">DG</option>
                            <option value="3">WM</option>
                          </select>
                        </div>
                      </div>
                      <button
                        onClick={handleUpdateTable}
                        className={isTableNumberLocked ? 'bg-gray-400' : ''}
                      >
                        Cập nhật
                      </button>
                    </div>

                    <div className={`flex justify-between ${styles.bacaratCaculatorInput}`}>
                      <div className={`w-2/3 pr-2`}>
                        <div className="flex items-center gap-2">
                          <p style={{ color: 'blue' }}>Con : </p>
                          <input
                            type="number"
                            value={playerValue}
                            onChange={(e) => setPlayerValue(e.target.value === '' ? '' : Number(e.target.value))}
                            disabled={isCalculating || !displayTableNumber}
                            className={!displayTableNumber ? 'bg-gray-200 cursor-not-allowed' : ''}
                          />
                        </div>

                        <div className="flex items-center gap-2">
                          <p style={{ color: 'red' }}>Cái : </p>
                          <input
                            type="number"
                            value={bankerValue}
                            onChange={(e) => setBankerValue(e.target.value === '' ? '' : Number(e.target.value))}
                            disabled={isCalculating || !displayTableNumber}
                            className={!displayTableNumber ? 'bg-gray-200 cursor-not-allowed' : ''}
                          />
                        </div>
                      </div>

                      <div className={`${styles.bacaratResultTotal} w-1/3 pr-2 flex items-center justify-center`}>
                        {isCalculating ? (
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-red-600"></div>
                        ) : (
                          resultTotal
                        )}
                      </div>
                    </div>

                    <div className={`flex justify-between ${styles.bacaratCaculatorCalculatorInput}`}>
                      <div className="w-1/4 p-2">
                        <button
                          onClick={handleCalculate}
                          disabled={playerValue === '' || bankerValue === '' || isCalculating || !displayTableNumber}
                          className={`${styles.btnCalculate} w-full h-24 font-bold shadow-md transition-all`}
                        >
                          TÍNH TOÁN
                        </button>
                      </div>
                      <div className="w-3/4 flex flex-col justify-center items-center text-center">
                        <p className={``}>Dự đoán kết quả tiếp theo</p>
                        {isCalculating ? (
                          <h4 className={`${styles.bacaratResultDetail} text-[30px] text-red-600 font-bold animate-pulse`}>
                            Công thức đang được tính toán ...
                          </h4>
                        ) : (
                          <h4 className={`${styles.bacaratResultDetail} text-[40px] ${resultDetail === "Con" ? "text-blue-600" : "text-red-600"} font-bold`}>
                            {resultDetail}
                          </h4>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Mobile layout (4 dòng + nút tính toán; bỏ ô điểm) */}
                  <div className="md:hidden px-3 pb-3">
                    {/* Dòng 1: 50/50 (Sảnh + chọn) | Cập nhật */}
                    <div className="flex gap-2 mt-2">
                      <div className="w-1/2">
                        <div className={styles.mobileInputWrapper}>
                          <span className={styles.mobileInputLabel}>Sảnh :</span>
                          <select className={styles.mobileSelectField}>
                            <option value="1">SEXY</option>
                            <option value="2">DG</option>
                            <option value="3">WM</option>
                          </select>
                        </div>
                      </div>
                      <div className="w-1/2">
                        <button
                          onClick={handleUpdateTable}
                          className={`${styles.btnCalculate} w-full h-[44px]`}
                          disabled={isTableNumberLocked}
                        >
                          Cập nhật
                        </button>
                      </div>
                    </div>

                    {/* Dòng 2: Số bàn full */}
                    <div className="mt-3">
                      <div className={styles.mobileInputWrapper}>
                        <span className={styles.mobileInputLabel}>Số bàn :</span>
                        <input
                          type="text"
                          value={tableNumber}
                          onChange={(e) => setTableNumber(e.target.value)}
                          className={styles.mobileInputField}
                        />
                      </div>
                    </div>

                    {/* Dòng 3: Con full */}
                    <div className="mt-3">
                      <div className={styles.mobileInputWrapper}>
                        <span className={styles.mobileInputLabel}>Con :</span>
                        <input
                          type="number"
                          value={playerValue}
                          onChange={(e) => setPlayerValue(e.target.value === '' ? '' : Number(e.target.value))}
                          disabled={isCalculating || !displayTableNumber}
                          className={styles.mobileInputField}
                        />
                      </div>
                    </div>

                    {/* Dòng 4: Cái full */}
                    <div className="mt-3">
                      <div className={styles.mobileInputWrapper}>
                        <span className={styles.mobileInputLabel}>Cái :</span>
                        <input
                          type="number"
                          value={bankerValue}
                          onChange={(e) => setBankerValue(e.target.value === '' ? '' : Number(e.target.value))}
                          disabled={isCalculating || !displayTableNumber}
                          className={styles.mobileInputField}
                        />
                      </div>
                    </div>

                    {/* Tính toán + dự đoán (1 hàng ngang) */}
                    <div className="flex gap-2 mt-4">
                      <div className="w-1/5">
                        <button
                          onClick={handleCalculate}
                          disabled={playerValue === '' || bankerValue === '' || isCalculating || !displayTableNumber}
                          className={`${styles.btnCalculate} w-full h-24`}
                        >
                          TÍNH TOÁN
                        </button>
                      </div>

                      <div className={`w-4/5 ${styles.predictionFrame}`}>
                        <div className={styles.predictionFrameTitle}>
                          DỰ ĐOÁN KẾT QUẢ
                          <br />
                          TIẾP THEO
                        </div>

                        <div className={styles.predictionMainRow}>
                          <div className={styles.predictionCircleWrapper}>
                            <div
                              className={`${styles.predictionCircle} ${
                                resultDetail === "Cái" ? styles.predictionCircleRed : styles.predictionCircleBlue
                              }`}
                            >
                              {isCalculating
                                ? "..."
                                : resultDetail === "Con"
                                  ? "P"
                                  : resultDetail === "Cái"
                                    ? "B"
                                    : ""}
                            </div>
                          </div>

                          <div className={styles.predictionMainText}>
                            {isCalculating
                              ? '...'
                              : resultDetail === "Con"
                                ? "TAY CON"
                                : resultDetail === "Cái"
                                  ? "TAY CÁI"
                                  : "—"}
                            <div className={styles.predictionRate}>
                              Tỷ lệ thắng : {isCalculating ? "..." : predictionWinPct}%
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <button onClick={handleReset} className={styles.btnReset}>RESET</button>
                </div>
              </div>
            </div>
          </div>

          <div className={`mt-2 md:mt-4`}>
            <section className="room-panel room-panel--history">
              <div className="room-history__head">
                <h3 className="room-history__title">
                  <span className="room-history__chev">&gt;&gt;</span> LỊCH SỬ
                  PHIÊN
                </h3>
                <span className="room-history__clock" title="Giờ hệ thống (VN)">
                  {liveClock}
                </span>
              </div>

              <div className="room-history">
                <table>
                  <thead>
                    <tr>
                      <th>PHIÊN</th>
                      <th>DỰ ĐOÁN</th>
                      <th>KẾT QUẢ</th>
                      <th>ĐÁNH GIÁ</th>
                      <th>THỜI GIAN</th>
                    </tr>
                  </thead>
                  <tbody>
                    {historyItems.map((item: any) => (
                      <tr key={item.key}>
                        <td className="room-history__session">{item.sessionId}</td>
                        <td>
                          <span className={`room-pill ${pillClassForSide(item.duDoan)}`}>
                            {item.duDoan}
                          </span>
                        </td>
                        <td>
                          <span className={`room-pill ${pillClassForSide(item.ketQua)}`}>
                            {item.ketQua}
                          </span>
                        </td>
                        <td>
                          <span className={`room-pill ${pillClassForDanhGia(item.danhGia)}`}>
                            {item.danhGia}
                          </span>
                        </td>
                        <td className="room-history__time">
                          {item.stampTime ? formatLogTime(item.stampTime) : liveClock}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          </div>
          </div>
          <ModalConfirmLogout
            isShowLogout={isShowLogout}
            setIsShowLogout={() => {
              setIsShowLogout(false);
            }}
          />
        </div>
      </div>
    </div>
  );
}
